import React from "react";
import {
  DealData,
  DealQuestionnaireQuestionSettings,
  QuestionResponse,
  Responses,
  Score
} from "@/pages/deals/types";
import SingleSelect, {
  SingleSelectOption
} from "@/components/controls/SingleSelect";
import { getFullName } from "@/entities/people/helpers";
import { usePeople, usePeopleByCompany } from "@/entities/people/hooks";
import { useUpdateDealQuestionnaireMutation } from "@/pages/deals/hooks";
import BaseQuestion from "./BaseQuestion";
import { isNull, map } from "lodash";

interface SingleSelectProps {
  question: DealQuestionnaireQuestionSettings;
  localResponses: Responses;
  updateResponse: (
    questionId: string,
    response: QuestionResponse | null
  ) => void;
  questionnaireId: string;
  handleUpdateQuestionnaireScore: (newValue: Score | null) => void;
  onResponseError: () => void;
  dealData?: DealData;
  className?: string;
}

const SingleSelectQuestion: React.FC<SingleSelectProps> = ({
  question,
  localResponses,
  updateResponse,
  questionnaireId,
  handleUpdateQuestionnaireScore,
  onResponseError,
  dealData,
  className
}) => {
  const updateQuestionnaire = useUpdateDealQuestionnaireMutation();

  if (!dealData) {
    return null;
  }

  const peopleInCompanyResponse = "peopleInCompany";
  const companyContactIds = usePeopleByCompany(dealData.companyId || "").data
    ?.ids;
  const companyPeople = usePeople(companyContactIds || []).entities;

  const getQuestionOptions: () => SingleSelectOption[] = () => {
    if (
      question.responseType?.type === peopleInCompanyResponse &&
      companyPeople
    ) {
      return map(companyPeople, (person) => ({
        id: person?.id as string,
        displayName: person ? getFullName(person) : "",
        subtitle: person?.jobTitle ? person?.jobTitle : undefined
      })).sort((a, b) => a.displayName.localeCompare(b.displayName));
    }
    return question.responseType?.options || [];
  };

  const options = getQuestionOptions();
  const answer = localResponses[question.id]?.response?.value;
  const stringOrNullAnswer: string | null =
    typeof answer === "string" || answer === null ? answer : null;
  // SingleSelect renders a value missing from the options as unselected.
  const isValueDisplayable =
    !stringOrNullAnswer ||
    options.some((option) => String(option.id) === stringOrNullAnswer);

  const handleSelect = (value: number | string | null) => {
    const selectedPersonId = value?.toString() ?? null;

    if (selectedPersonId === stringOrNullAnswer) return;

    const existingResponse =
      localResponses[question.id] || ({} as QuestionResponse | null);
    const newResponse = {
      ...existingResponse,
      response: { value: selectedPersonId },
      lastEditedBy: undefined
    };

    updateResponse(question.id, newResponse);

    updateQuestionnaire.mutate(
      {
        pathParams: { dealId: dealData.id, questionnaireId },
        data: {
          questions: [
            {
              questionId: question.id,
              ...newResponse
            }
          ]
        }
      },
      {
        onSuccess: (data) => {
          handleUpdateQuestionnaireScore(data.score);
        },
        onError: onResponseError
      }
    );
  };

  return (
    <BaseQuestion
      question={question}
      questionnaireId={questionnaireId}
      dealId={dealData.id}
      handleUpdateQuestionnaireScore={handleUpdateQuestionnaireScore}
      localResponses={localResponses}
      updateResponse={updateResponse}
      onResponseError={onResponseError}
      className={className}
      isValueDisplayable={isValueDisplayable}
    >
      <SingleSelect
        fieldName={question.id}
        options={options}
        selectedValue={
          isNull(stringOrNullAnswer) ? undefined : stringOrNullAnswer
        }
        onSelection={handleSelect}
        variant="form"
        showSearch
        sideOffset={0}
        clearable
        onClear={() => handleSelect(null)}
        placeholder={question.responseType?.placeholder}
      />
    </BaseQuestion>
  );
};

export default SingleSelectQuestion;
